import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import {
  RotateCcw,
  CheckCircle2,
  XCircle,
  Clock,
  Building2,
  MapPin,
  CreditCard,
  RefreshCw,
  WifiOff,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { formatDate } from "@/lib/demo-data";
import { toast } from "sonner";

function daysSince(date: string | Date): number {
  return Math.floor(
    (Date.now() - new Date(date).getTime()) / (1000 * 60 * 60 * 24)
  );
}

export default function Refunds() {
  const { user } = useAuth();
  const isWriter = canWrite(user?.staffRole || null);

  const [tab, setTab] = useState<"queue" | "history">("queue");
  const [queuePage, setQueuePage] = useState(1);
  const [historyPage, setHistoryPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Batch refund dialog
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);
  const [batchReason, setBatchReason] = useState("");

  // Single refund dialog
  const [singleRefundId, setSingleRefundId] = useState<string | null>(null);
  const [singleReason, setSingleReason] = useState("");

  // History date filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const {
    data: queueData,
    isLoading: queueLoading,
    isError: queueError,
    refetch: refetchQueue,
  } = trpc.admin.travauxbtp.listRefundQueue.useQuery(
    { page: queuePage, limit: 20 },
    { enabled: tab === "queue" }
  );

  const {
    data: historyData,
    isLoading: historyLoading,
    isError: historyError,
    refetch: refetchHistory,
  } = trpc.admin.travauxbtp.listRefundHistory.useQuery(
    {
      page: historyPage,
      limit: 20,
      ...(startDate ? { startDate: new Date(startDate).toISOString() } : {}),
      ...(endDate ? { endDate: new Date(endDate).toISOString() } : {}),
    },
    { enabled: tab === "history" }
  );

  const batchRefundMutation = trpc.admin.travauxbtp.batchRefund.useMutation({
    onSuccess: (result) => {
      toast.success(`${result.refunded} crédit(s) remboursé(s)`);
      setSelectedIds(new Set());
      setBatchDialogOpen(false);
      setBatchReason("");
      setSingleRefundId(null);
      setSingleReason("");
      refetchQueue();
      refetchHistory();
    },
    onError: (err) => toast.error(err.message),
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!queueData?.items) return;
    if (selectedIds.size === queueData.items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(queueData.items.map((i) => i.id)));
    }
  };

  const handleBatchRefund = () => {
    if (!batchReason.trim()) return;
    batchRefundMutation.mutate({
      matchIds: Array.from(selectedIds),
      reason: batchReason.trim(),
    });
  };

  const handleSingleRefund = () => {
    if (!singleRefundId || !singleReason.trim()) return;
    batchRefundMutation.mutate({
      matchIds: [singleRefundId],
      reason: singleReason.trim(),
    });
  };

  const queueTotal = queueData?.total ?? 0;
  const queueTotalPages = Math.ceil(queueTotal / 20);
  const historyTotal = historyData?.total ?? 0;
  const historyTotalPages = Math.ceil(historyTotal / 20);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">
              Remboursements
            </h1>
            <p className="text-muted-foreground text-sm">
              File d'attente et historique des remboursements crédits
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              refetchQueue();
              refetchHistory();
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-orange-100 p-2.5">
                  <Clock className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">En attente</p>
                  <p className="text-2xl font-bold">{queueTotal}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-100 p-2.5">
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Sélectionnés
                  </p>
                  <p className="text-2xl font-bold">{selectedIds.size}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-green-100 p-2.5">
                  <CreditCard className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">
                    Crédits sélectionnés
                  </p>
                  <p className="text-2xl font-bold">
                    {queueData?.items
                      .filter((i) => selectedIds.has(i.id))
                      .reduce((sum, i) => sum + i.creditAmount, 0) ?? 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs
          value={tab}
          onValueChange={(v) => setTab(v as "queue" | "history")}
        >
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="queue">
                File d'attente
                {queueTotal > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 px-1.5">
                    {queueTotal}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="history">Historique</TabsTrigger>
            </TabsList>

            {tab === "queue" && selectedIds.size > 0 && isWriter && (
              <Button
                onClick={() => setBatchDialogOpen(true)}
                className="bg-green-600 hover:bg-green-700"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Rembourser {selectedIds.size} sélection(s)
              </Button>
            )}
          </div>

          {/* Queue Tab */}
          <TabsContent value="queue" className="mt-4">
            {queueLoading ? (
              <ListPageSkeleton />
            ) : queueError ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <WifiOff className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Erreur de chargement
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => refetchQueue()}
                  >
                    Réessayer
                  </Button>
                </CardContent>
              </Card>
            ) : !queueData || queueData.items.length === 0 ? (
              <EmptyState
                icon={<CheckCircle2 className="h-8 w-8" />}
                title="Aucun remboursement en attente"
                description="Tous les leads ont été traités. La file est vide."
              />
            ) : (
              <>
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {isWriter && (
                          <TableHead className="w-10">
                            <input
                              type="checkbox"
                              checked={
                                queueData!.items.length > 0 &&
                                selectedIds.size === queueData!.items.length
                              }
                              onChange={toggleSelectAll}
                              className="h-4 w-4 rounded border-gray-300"
                            />
                          </TableHead>
                        )}
                        <TableHead>Entreprise</TableHead>
                        <TableHead>Projet</TableHead>
                        <TableHead>Crédits</TableHead>
                        <TableHead>Jours depuis unlock</TableHead>
                        <TableHead>Éligibilité</TableHead>
                        {isWriter && (
                          <TableHead className="text-right">Actions</TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {queueData!.items.map((item) => {
                        const days = item.unlockedAt
                          ? daysSince(item.unlockedAt)
                          : 0;
                        return (
                          <TableRow key={item.id}>
                            {isWriter && (
                              <TableCell>
                                <input
                                  type="checkbox"
                                  checked={selectedIds.has(item.id)}
                                  onChange={() => toggleSelect(item.id)}
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                              </TableCell>
                            )}
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                                <div>
                                  <div className="font-medium text-sm">
                                    {item.artisan.company}
                                  </div>
                                  {item.artisan.email && (
                                    <div className="text-xs text-muted-foreground">
                                      {item.artisan.email}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="text-sm">
                                  {item.project.title}
                                </div>
                                {item.project.city && (
                                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <MapPin className="h-3 w-3" />
                                    {item.project.city}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="secondary">
                                {item.creditAmount} crédit
                                {item.creditAmount > 1 ? "s" : ""}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <span
                                className={
                                  days > 14
                                    ? "text-red-600 font-medium"
                                    : "text-muted-foreground"
                                }
                              >
                                {days}j
                              </span>
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className="text-orange-600 border-orange-300"
                              >
                                Auto-éligible
                              </Badge>
                            </TableCell>
                            {isWriter && (
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                    onClick={() => {
                                      setSingleRefundId(item.id);
                                      setSingleReason("");
                                    }}
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                                    Approuver
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </Card>

                {/* Pagination */}
                {queueTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      {queueTotal} résultat{queueTotal > 1 ? "s" : ""} — page{" "}
                      {queuePage}/{queueTotalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={queuePage <= 1}
                        onClick={() => setQueuePage((p) => p - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={queuePage >= queueTotalPages}
                        onClick={() => setQueuePage((p) => p + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-4">
            {/* Date filters */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Du</label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    setStartDate(e.target.value);
                    setHistoryPage(1);
                  }}
                  className="w-40"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Au</label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setHistoryPage(1);
                  }}
                  className="w-40"
                />
              </div>
              {(startDate || endDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                    setHistoryPage(1);
                  }}
                >
                  <XCircle className="h-4 w-4 mr-1" />
                  Réinitialiser
                </Button>
              )}
            </div>

            {historyLoading ? (
              <ListPageSkeleton />
            ) : historyError ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <WifiOff className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    Erreur de chargement
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() => refetchHistory()}
                  >
                    Réessayer
                  </Button>
                </CardContent>
              </Card>
            ) : !historyData || historyData.items.length === 0 ? (
              <EmptyState
                icon={<RotateCcw className="h-8 w-8" />}
                title="Aucun remboursement"
                description="Aucun remboursement trouvé pour la période sélectionnée."
              />
            ) : (
              <>
                <Card>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Entreprise</TableHead>
                        <TableHead>Projet</TableHead>
                        <TableHead>Ville</TableHead>
                        <TableHead>Crédits</TableHead>
                        <TableHead>Date unlock</TableHead>
                        <TableHead>Date remboursement</TableHead>
                        <TableHead>Raison</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyData!.items.map((item) => (
                        <TableRow key={item.matchId}>
                          <TableCell className="font-medium text-sm">
                            {item.companyName ?? "—"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {item.projectTitle ?? "—"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.projectCity ?? "—"}
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {item.creditPricePaid ?? 1} crédit
                              {(item.creditPricePaid ?? 1) > 1 ? "s" : ""}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {item.unlockedAt
                              ? formatDate(item.unlockedAt)
                              : "—"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {item.refundedAt
                              ? formatDate(item.refundedAt)
                              : "—"}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                            {item.refundReason ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Card>

                {/* Pagination */}
                {historyTotalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      {historyTotal} résultat{historyTotal > 1 ? "s" : ""} —
                      page {historyPage}/{historyTotalPages}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage <= 1}
                        onClick={() => setHistoryPage((p) => p - 1)}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={historyPage >= historyTotalPages}
                        onClick={() => setHistoryPage((p) => p + 1)}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Batch Refund Dialog */}
      <Dialog open={batchDialogOpen} onOpenChange={setBatchDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Rembourser {selectedIds.size} match
              {selectedIds.size > 1 ? "es" : ""}
            </DialogTitle>
            <DialogDescription>
              Les crédits seront restitués aux entreprises concernées. Cette
              action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-medium">Raison du remboursement</label>
            <Textarea
              value={batchReason}
              onChange={(e) => setBatchReason(e.target.value)}
              placeholder="Ex: Lead non répondu après 7 jours (garantie automatique)"
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {batchReason.length}/500 caractères
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBatchDialogOpen(false)}
            >
              Annuler
            </Button>
            <Button
              onClick={handleBatchRefund}
              disabled={
                !batchReason.trim() || batchRefundMutation.isPending
              }
              className="bg-green-600 hover:bg-green-700"
            >
              {batchRefundMutation.isPending
                ? "Remboursement..."
                : `Rembourser ${selectedIds.size} crédit(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Single Refund Dialog */}
      <Dialog
        open={!!singleRefundId}
        onOpenChange={(open) => {
          if (!open) {
            setSingleRefundId(null);
            setSingleReason("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approuver le remboursement</DialogTitle>
            <DialogDescription>
              Le crédit sera restitué à l'entreprise. Cette action est
              irréversible.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <label className="text-sm font-medium">Raison du remboursement</label>
            <Textarea
              value={singleReason}
              onChange={(e) => setSingleReason(e.target.value)}
              placeholder="Ex: Lead non répondu après 7 jours (garantie automatique)"
              rows={3}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {singleReason.length}/500 caractères
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setSingleRefundId(null);
                setSingleReason("");
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleSingleRefund}
              disabled={
                !singleReason.trim() || batchRefundMutation.isPending
              }
              className="bg-green-600 hover:bg-green-700"
            >
              {batchRefundMutation.isPending
                ? "Remboursement..."
                : "Confirmer le remboursement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
